import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ArrowLeft } from "@/components/icons";
import { fetchUser } from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { UserDetailPanel } from "@/views/UserDetailPanel";
import { useT } from "@/app/localization";

/**
 * Full details page for a single user, addressable at /users/:userId.
 *
 * Lets other pages (e.g. an organization's member list) link straight to a
 * user instead of only reaching the detail through the Users list slide panel.
 * Renders the same shared UserDetailPanel used there.
 */
export function UserDetailPage() {
  const t = useT();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const id = Number(userId);

  const userQ = useQuery({
    queryKey: ["user", id],
    queryFn: () => fetchUser(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  if (!userId || Number.isNaN(id)) {
    return (
      <div className="p-5 pt-[86px]">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t("operator.users.invalid_id")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 pt-[86px]">
      <div>
        <Button variant="ghost" size="sm" className="gap-1 mb-2" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4" /> {t("operator.unit.back_to", { page: t("operator.nav.users") })}
          </Link>
        </Button>
      </div>

      <div className="max-w-3xl">
        {userQ.isLoading && <div className="text-sm opacity-60">{t("common.state.loading")}</div>}
        {userQ.isError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("operator.users.load_one_failed")}
          </div>
        )}
        {userQ.data && (
          <UserDetailPanel
            user={userQ.data}
            isSelf={currentUser?.id === userQ.data.id}
            onDeleted={() => navigate("/users")}
          />
        )}
      </div>
    </div>
  );
}
